import {
  HOST_QUERY_KEYS,
  reportablePath,
  validAppPath,
  type HostProvider,
} from '@tasks/bridge-client/host';
import { resourceOfPath } from './route-resource.js';
import type { HostMode } from './mode.js';

/**
 * EMBED-ROUTE — inside Vitto this app lives in an iframe whose history disappears when the host unmounts it (Desks ⇄ Page). So the app SAYS where
 * it is (every resolved navigation, and again once the host's first INIT gave the generation) and, when the host frames it again, restores
 * what the host handed back as `?path=`. Both sides validate the path: neither trusts the wire or the storage in between.
 */
type Win = {
  location: { pathname: string; search: string };
  history: { state: unknown; replaceState: (state: unknown, unused: string, url: string) => void };
};

/**
 * At boot, BEFORE the router exists: a fresh mount (the app's root) of an embedded document with a valid `?path=` starts there instead.
 * The host's own coordinates (embedded, parentOrigin, workspace, space) stay in the URL; `path` itself is consumed. Returns the path used.
 */
export function restoreEmbeddedRoute(win: Win, mode: HostMode): string | null {
  if (mode.kind !== 'embedded' || win.location.pathname !== '/') return null;
  const current = new URLSearchParams(win.location.search);
  const path = validAppPath(current.get('path'));
  if (path === null) return null;
  const target = new URL(path, 'https://app.invalid');
  for (const key of HOST_QUERY_KEYS.filter((k) => k !== 'path')) {
    const value = current.get(key);
    if (value !== null) target.searchParams.set(key, value);
  }
  try {
    win.history.replaceState(win.history.state, '', `${target.pathname}${target.search}`);
  } catch {
    // GABO C1: a URL the browser refuses (a SecurityError) must never stop the app from starting: it starts at its root, as before.
    return null;
  }
  return path;
}

type Resolvable = { subscribe: (event: 'onResolved', fn: () => void) => () => void };

/** Report the current route to the host now and after every resolved navigation (and when the host's state moves: the first INIT). */
export function reportEmbeddedRoutes(win: Win, router: Resolvable, host: HostProvider): () => void {
  let last: string | null = null;
  const report = (): void => {
    const path = reportablePath(
      win.location.pathname === '' ? { pathname: '/', search: win.location.search } : win.location,
    );
    if (path === null || path === last) return;
    // APP-CONTEXT-DESK-1: what the route says is OPEN rides with it (by reference); a place with nothing open reports the path alone
    const resource = resourceOfPath(path);
    if (resource === undefined) host.reportRoute?.(path);
    else host.reportRoute?.(path, resource);
    // «last» only advances once the host could take it: before the first INIT reportRoute is a no-op and the same path must be sent again
    if (host.getState() === 'ready') last = path;
  };
  const stopRouter = router.subscribe('onResolved', report);
  const stopHost = host.subscribe(report);
  report();
  return () => {
    stopRouter();
    stopHost();
  };
}
